import {
  Button,
  TextField,
  MenuItem,
  Box,
  DialogActions,
  DialogContent,
  Stack,
  DialogTitle,
  InputAdornment,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  dateToServer,
  renderDateUTC,
  renderTimeUTC,
} from "../../../utils/utils";
import { useAuthUser } from "react-auth-kit";
import { useGetDoctors } from "../../DoctorComponents";
import { useNewPatient } from "../usePatientQueries";
import { useGetOrganizations } from "../../HandlingComponents";

function AddPatient({ closeModal }) {
  const authUser = useAuthUser();
  const isDoctor = authUser()?.role === 3;

  // === web service queries/mutations ===
  const doctorsQuery = useGetDoctors({ enabled: !isDoctor, active: true });
  const organizationQuery = useGetOrganizations();

  const newPatientMutation = useNewPatient(closeModal);

  // ==== TODO: doctor_id should be 'required' for admin/superadmin ====
  const validationSchema = yup.object({
    first_name: yup
      .string("Nome (*)")
      .required("Nome è un campo obbligatorio!"),
    last_name: yup
      .string("Cognome (*)")
      .required("Cognome è un campo obbligatorio!"),
    gender: yup
      .string("Genere (*)")
      .required("Genere è un campo obbligatorio!"),
    cf: yup
      .string("Codice Fiscale (*)")
      .required("Codice Fiscale è un campo obbligatorio!"),
    disclaimer: yup.number().required("Disclaimer è un campo obbligatorio!"),
    height: yup.number().required().positive(),
    weight: yup.number().required().positive(),
    org_id: authUser()?.role === 1 && yup.number().required(),
  });

  // new patient form handling
  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      cf: "",
      gender: "",
      doctor_id: null, // temp - must fix doctor_id required if super/admin
      org_id: null,
      date_of_birth: null,
      height: "",
      weight: "",
      hypertensive: false,
      disclaimer: 1,
      note: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // get doctor id, either from the form or from auth object
      let doc =
        authUser()?.role === 3 ? authUser().user.doctor_id : values.doctor_id;

      // create new Patient object
      let newPatient = {
        ...values,
        doctor_id: doc,
        date_of_birth: dateToServer(values.date_of_birth),
      };

      // add org_id if superadmin
      // if (authUser()?.role === 1) {
      //   newPatient = {
      //     ...newPatient,
      //   };
      // }
      newPatientMutation.mutate(newPatient);
    },
  });

  const doctorsList = doctorsQuery.data?.doctors || [];
  const organizationsList = organizationQuery.data?.organizations || [];

  const filteredDoctors = formik.values.org_id
    ? doctorsList.filter((doc) => doc.org_id === formik.values.org_id)
    : doctorsList;

  const selectedDoctorOrgId =
    formik.values.doctor_id !== null
      ? doctorsList.find((doc) => doc.id === formik.values.doctor_id)?.org_id ??
        null
      : null;

  const filteredOrganizations =
    authUser()?.role === 1 && organizationsList.length > 0
      ? selectedDoctorOrgId !== null
        ? organizationsList.filter((org) => org.id === selectedDoctorOrgId)
        : organizationsList
      : [];

  const handleOrgChange = (event) => {
  const selectedOrgId =
    event.target.value === "" ? null : Number(event.target.value);

  formik.setFieldValue("org_id", selectedOrgId, true);
};

const handleDoctorChange = (event) => {
  const selectedDoctorId =
    event.target.value === "" ? null : Number(event.target.value);

  formik.setFieldValue("doctor_id", selectedDoctorId, true);
};

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogTitle>Nuovo paziente</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={8} sx={{ width: "100%" }}>
          <Box sx={{ float: "left", width: "50%" }}>
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              id="first_name"
              name="first_name"
              label="Nome (*)"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.first_name}
              error={
                formik.touched.first_name && Boolean(formik.errors.first_name)
              }
              helperText={formik.touched.first_name && formik.errors.first_name}
            />

            <TextField
              margin="dense"
              fullWidth
              id="last_name"
              name="last_name"
              label="Cognome (*)"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.last_name}
              error={
                formik.touched.last_name && Boolean(formik.errors.last_name)
              }
              helperText={formik.touched.last_name && formik.errors.last_name}
            />

            <TextField
              margin="dense"
              fullWidth
              id="cf"
              name="cf"
              label="Codice Fiscale (*)"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.cf}
              error={formik.touched.cf && Boolean(formik.errors.cf)}
              helperText={formik.touched.cf && formik.errors.cf}
            />

            <TextField
              margin="dense"
              fullWidth
              id="gender"
              name="gender"
              label="Genere (*)"
              select
              variant="standard"
              defaultValue={""}
              onChange={formik.handleChange}
              value={formik.values.gender}
              error={formik.touched.gender && Boolean(formik.errors.gender)}
              helperText={formik.touched.gender && formik.errors.gender}
            >
              <MenuItem value={"M"}>Maschio</MenuItem>
              <MenuItem value={"F"}>Femmina</MenuItem>
            </TextField>

            {/* Only superadmin/admin can choose doctor */}
            {authUser()?.role !== 3 && !doctorsQuery.isLoading && (
              <TextField
                select
                fullWidth
                margin="dense"
                id="doctor_id"
                name="doctor_id"
              label="Professionista Sanitario"
              defaultValue={""}
              value={formik.values.doctor_id}
              onChange={handleDoctorChange}
              error={
                formik.touched.doctor_id && Boolean(formik.errors.doctor_id)
              }
              helperText={formik.touched.doctor_id && formik.errors.doctor_id}
              variant="standard"
            >
              {filteredDoctors.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.first_name} {option.last_name}
                </MenuItem>
              ))}
            </TextField>
            )}

            <DatePicker
              id="date_of_birth"
              name="date_of_birth"
              format="DD/MM/YYYY"
              disableFuture
              label="Data di nascita (*)"
              sx={{ marginTop: "2ch" }}
              value={formik.values.date_of_birth}
              onChange={(value) => {
                formik.setFieldValue("date_of_birth", value, true);
              }}
              slotProps={{
                textField: {
                  variant: "standard",
                  error:
                    formik.touched.date_of_birth &&
                    Boolean(formik.errors.date_of_birth),
                  helperText:
                    formik.touched.date_of_birth && formik.errors.date_of_birth,
                },
              }}
            />

            {/* Only superadmin can choose organization */}
            {authUser()?.role === 1 && organizationQuery.isSuccess && (
            <TextField
      margin="dense"
      fullWidth
      select
      id="org_id"
      name="org_id"
      label="Organizzazione (*)"
      variant="standard"
      onChange={handleOrgChange}
      value={formik.values.org_id || ""}
      error={formik.touched.org_id && Boolean(formik.errors.org_id)}
      helperText={formik.touched.org_id && formik.errors.org_id}
    >
      <MenuItem value="">
        <em>Nessuna organizzazione</em>
      </MenuItem>
      {filteredOrganizations.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
            )}
          </Box>

          <Box sx={{ float: "right", width: "50%" }}>
            <TextField
              margin="dense"
              fullWidth
              id="height"
              name="height"
              label="Altezza (*)"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.height}
              error={formik.touched.height && Boolean(formik.errors.height)}
              helperText={formik.touched.height && formik.errors.height}
              sx={{ m: 1, width: "20ch" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">cm</InputAdornment>
                ),
              }}
            />

            <TextField
              margin="dense"
              fullWidth
              id="weight"
              name="weight"
              label="Peso (*)"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.weight}
              error={formik.touched.weight && Boolean(formik.errors.weight)}
              helperText={formik.touched.weight && formik.errors.weight}
              sx={{ m: 1, width: "20ch" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">kg</InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              sx={{
                margin: "8px",
              }}
              control={
                <Switch
                  name="hypertensive"
                  value={formik.values.hypertensive}
                  checked={formik.values.hypertensive}
                  onChange={formik.handleChange}
                />
              }
              label="Iperteso"
              labelPlacement="start"
            />

            <TextField
              margin="dense"
              fullWidth
              id="note"
              sx={{
                margin: "8px",
              }}
              name="note"
              label="Anamnesi - Note"
              multiline
              minRows={4}
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.note}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={newPatientMutation.isLoading} type="submit">
          Conferma
        </Button>
        <Button
          disabled={newPatientMutation.isLoading}
          onClick={formik.handleReset}
        >
          Reset
        </Button>
        <Button
          disabled={newPatientMutation.isLoading}
          color="error"
          onClick={closeModal}
        >
          Annulla
        </Button>
      </DialogActions>
    </form>
  );
}

export default AddPatient;
